import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Truck, Plus, Trash2, Scale } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DispatchPage = ({ user, token }) => {
  const [hubs, setHubs] = useState([]);
  const [selectedHub, setSelectedHub] = useState(user?.hub_id || "");
  const [materialTypes, setMaterialTypes] = useState([]);
  const [vendorName, setVendorName] = useState("");
  const [vehicleNumber, setVehicleNumber] = useState("");
  const [items, setItems] = useState([{ material_type: "", weight: "" }]);
  const [dispatchList, setDispatchList] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchHubs();
    fetchDispatchList();
    fetchMaterialTypes();
  }, []);

  const fetchMaterialTypes = async () => {
    try {
      const response = await axios.get(`${API}/material-types`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMaterialTypes(response.data);
    } catch (error) {
      console.error("Error fetching material types:", error);
    }
  };

  const fetchHubs = async () => {
    try {
      const response = await axios.get(`${API}/hubs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHubs(response.data);
      if (response.data.length > 0 && !user?.hub_id) {
        setSelectedHub(response.data[0].id);
      }
    } catch (error) {
      console.error("Error fetching hubs:", error);
    }
  };

  const fetchDispatchList = async () => {
    try {
      const response = await axios.get(`${API}/dispatch`, {
        headers: { Authorization: `Bearer ${token}` },
        params: user?.hub_id ? { hub_id: user.hub_id } : {}
      });
      setDispatchList(response.data);
    } catch (error) {
      console.error("Error fetching dispatch list:", error);
    }
  };

  const addItem = () => {
    setItems([...items, { material_type: "", weight: "" }]);
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const updateItem = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const calculateTotalWeight = () => {
    return items.reduce((sum, item) => {
      return sum + (parseFloat(item.weight) || 0);
    }, 0).toFixed(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!vendorName || !vehicleNumber) {
      toast.error("Please fill vendor name and vehicle number");
      return;
    }

    const validItems = items.filter(item => item.material_type && item.weight);
    if (validItems.length === 0) {
      toast.error("Please add at least one valid item");
      return;
    }

    setLoading(true);

    try {
      await axios.post(
        `${API}/dispatch/batch`,
        {
          hub_id: selectedHub,
          vendor_name: vendorName,
          vehicle_number: vehicleNumber,
          items: validItems.map(item => ({
            material_type: item.material_type,
            weight: parseFloat(item.weight)
          }))
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Dispatched ${validItems.length} materials!`);
      setVendorName("");
      setVehicleNumber("");
      setItems([{ material_type: "", weight: "" }]);
      fetchDispatchList();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create entries");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="dispatch-page">
      <div>
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-heading tracking-tight">Dispatch</h1>
        <p className="text-muted-foreground text-sm sm:text-base mt-1">Track outgoing materials</p>
      </div>

      <Card className="shadow-lg border-border/50">
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-lg sm:text-xl lg:text-2xl font-heading">New Dispatch</CardTitle>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 pt-0">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {!user?.hub_id && (
                <div className="space-y-2">
                  <Label htmlFor="hub" className="text-sm">Hub</Label>
                  <Select value={selectedHub} onValueChange={setSelectedHub}>
                    <SelectTrigger data-testid="dispatch-hub-select">
                      <SelectValue placeholder="Select Hub" />
                    </SelectTrigger>
                    <SelectContent>
                      {hubs.map((hub) => (
                        <SelectItem key={hub.id} value={hub.id}>
                          {hub.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="vendor_name" className="text-sm">Vendor Name</Label>
                <Input
                  id="vendor_name"
                  data-testid="dispatch-vendor-input"
                  type="text"
                  placeholder="Enter vendor"
                  value={vendorName}
                  onChange={(e) => setVendorName(e.target.value)}
                  className="h-9 sm:h-10"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="vehicle_number" className="text-sm">Vehicle Number</Label>
                <Input
                  id="vehicle_number"
                  data-testid="dispatch-vehicle-input"
                  type="text"
                  placeholder="e.g., MH-01-AB-1234"
                  value={vehicleNumber}
                  onChange={(e) => setVehicleNumber(e.target.value)}
                  className="h-9 sm:h-10"
                  required
                />
              </div>
            </div>

            {/* Multiple Materials Section */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold">Materials in Load</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addItem}
                  className="gap-1 h-8 text-xs"
                  data-testid="add-dispatch-item-button"
                >
                  <Plus className="w-3 h-3" />
                  Add Material
                </Button>
              </div>

              {items.map((item, index) => (
                <Card key={index} className="border-dashed">
                  <CardContent className="p-3">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 grid grid-cols-2 gap-2">
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">Material Type</Label>
                          <Select
                            value={item.material_type}
                            onValueChange={(value) => updateItem(index, 'material_type', value)}
                          >
                            <SelectTrigger data-testid={`dispatch-material-${index}`} className="h-8 text-xs">
                              <SelectValue placeholder="Select material" />
                            </SelectTrigger>
                            <SelectContent>
                              {materialTypes.length > 0 ? (
                                materialTypes.map((material) => (
                                  <SelectItem key={material.id} value={material.name}>
                                    {material.name}
                                  </SelectItem>
                                ))
                              ) : (
                                <>
                                  <SelectItem value="Plastic">Plastic</SelectItem>
                                  <SelectItem value="Paper">Paper</SelectItem>
                                  <SelectItem value="Metal">Metal</SelectItem>
                                  <SelectItem value="Glass">Glass</SelectItem>
                                  <SelectItem value="Organic">Organic</SelectItem>
                                  <SelectItem value="E-Waste">E-Waste</SelectItem>
                                </>
                              )}
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">Weight (kg)</Label>
                          <Input
                            data-testid={`dispatch-weight-${index}`}
                            type="number"
                            step="0.01"
                            placeholder="0"
                            value={item.weight}
                            onChange={(e) => updateItem(index, 'weight', e.target.value)}
                            className="h-8 text-xs"
                          />
                        </div>
                      </div>

                      {items.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeItem(index)}
                          className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                          data-testid={`remove-dispatch-item-${index}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Card className="bg-gradient-to-br from-primary/10 to-accent/10 border-primary/20">
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Scale className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                    <span className="font-medium text-sm sm:text-base">Total Weight ({items.filter(i => i.material_type).length} materials)</span>
                  </div>
                  <div className="text-xl sm:text-2xl lg:text-3xl font-bold font-heading text-primary" data-testid="dispatch-total-weight">
                    {calculateTotalWeight()} kg
                  </div>
                </div>
              </CardContent>
            </Card>

            <Button
              data-testid="dispatch-submit-button"
              type="submit"
              className="w-full h-9 sm:h-10"
              disabled={loading}
            >
              {loading ? "Processing..." : `Dispatch ${items.filter(i => i.material_type && i.weight).length} Materials`}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card className="shadow-lg border-border/50">
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-lg sm:text-xl lg:text-2xl font-heading">Recent Dispatches</CardTitle>
        </CardHeader>
        <CardContent className="p-0 sm:p-4 lg:p-6 sm:pt-0">
          <div className="overflow-x-auto">
            <Table data-testid="dispatch-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs sm:text-sm">Date</TableHead>
                  <TableHead className="text-xs sm:text-sm">Vendor</TableHead>
                  <TableHead className="text-xs sm:text-sm">Material</TableHead>
                  <TableHead className="text-xs sm:text-sm">Weight</TableHead>
                  <TableHead className="text-xs sm:text-sm">Vehicle</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {dispatchList.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground text-sm">
                      No entries yet
                    </TableCell>
                  </TableRow>
                ) : (
                  dispatchList.slice(0, 15).map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell className="text-xs sm:text-sm">{entry.date}</TableCell>
                      <TableCell className="text-xs sm:text-sm">{entry.vendor_name}</TableCell>
                      <TableCell className="text-xs sm:text-sm">{entry.material_type}</TableCell>
                      <TableCell className="text-xs sm:text-sm">{entry.weight}kg</TableCell>
                      <TableCell className="text-xs sm:text-sm">{entry.vehicle_number}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DispatchPage;
